import { type ApiError, createClient } from "@majitap/sdk";

/**
 * The API, on the console's own origin: src/server (and Vite in development) proxies `/v1` and
 * `/api/auth` to it, so better-auth's session cookie is first-party and no token is stored in
 * the browser.
 */
export const createConsoleApi = (origin: string) => createClient({ baseUrl: origin, clientApp: "admin" });

export const api = createConsoleApi(globalThis.location?.origin ?? "");

export type ApiErrorBody = ApiError["error"];

/**
 * The `{ code, message }` of a failed call: from the API's `{ error: { … } }` body, or already
 * unwrapped (query functions throw `errorOf(error)`, and pages read it back with `errorOf`), or a
 * network failure in the same shape.
 */
export function errorOf(error: unknown): ApiErrorBody {
  const wrapped = (error as Partial<ApiError> | undefined)?.error;
  if (wrapped && typeof wrapped.code === "string") return wrapped;
  const bare = error as Partial<ApiErrorBody> | undefined;
  if (bare && typeof bare.code === "string" && typeof bare.message === "string") return bare as ApiErrorBody;
  return {
    code: "NETWORK_ERROR",
    message: "The console can't reach the MajiTap API. Check your connection and try again.",
  };
}

/**
 * A call's body, or its error thrown as `{ code, message }`. A delete answers 204 with no body:
 * that's a success, so it resolves to undefined.
 */
export async function ok<T>(request: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await request;
  if (error) throw errorOf(error);
  if (data === undefined && response.status !== 204) throw errorOf(undefined);
  return data as T;
}
